/**
 * The composer's audio brief — PURE state and copy for the Generate source of
 * the `music` and `sfx` kinds (plan rows 1.5–1.8, 1.11; ADR 0009).
 *
 * The form holds strings the creator types; `briefFromForm` turns them into
 * the closed `MusicBriefV1` / `SfxBriefV1` records through the SAME validators
 * the kickoff and the adapters run, so the button is disabled for exactly the
 * reasons the server would refuse. The saved "avoid this audio direction" line
 * is never part of the payload — the kickoff folds it in from the creation —
 * but the client-side length check includes it, because the rendered provider
 * text the server bounds will.
 */
import {
  MAX_INSTRUMENTS,
  SFX_CHARACTERS,
  audioGenerationEnabled,
  renderAudioBriefText,
  validateMusicBriefV1,
  validateSfxBriefV1,
  withAvoidDirection,
  type AudioBriefRefV1,
  type AudioGenerationFlags,
  type AudioGenerationKind,
  type GameDesign,
  type MusicBriefV1,
  type SfxBriefV1,
  type SfxCharacter,
} from '@djgg/creations';

export interface MusicBriefForm {
  mood: string;
  genre: string;
  /** Kept as typed text so a half-typed number never snaps to a default. */
  tempoBpm: string;
  /** Comma-separated instrument names. */
  instrumentation: string;
  referenceFeel: string;
}
export interface SfxBriefForm {
  description: string;
  character: SfxCharacter;
}
export type AudioBriefForm =
  | { kind: 'music'; form: MusicBriefForm }
  | { kind: 'sfx'; form: SfxBriefForm };

export const AUDIO_KINDS: readonly AudioGenerationKind[] = ['music', 'sfx'];
export function isAudioKind(kind: string): kind is AudioGenerationKind {
  return kind === 'music' || kind === 'sfx';
}

/** The music brief's first free-text line, and the SFX description, are each
 *  bounded to this by the port; a wizard direction is clamped to fit. */
const PREFILL_LINE_CHARS = 80;
function clampLine(text: string | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim().slice(0, PREFILL_LINE_CHARS).trim();
}

/**
 * Row 1.7: the wizard's music / sound-effects direction PREFILLS the brief's
 * first line after the first build. The wizard stores free text; the brief's
 * lines are bounded, so the text is clamped and the creator edits from there.
 * Nothing is sent by prefilling — only the explicit Generate action sends.
 */
export function briefFormPrefill(kind: AudioGenerationKind, audio: GameDesign['audio'] | undefined): AudioBriefForm {
  return kind === 'music'
    ? { kind, form: { mood: clampLine(audio?.musicBrief), genre: '', tempoBpm: '120', instrumentation: '', referenceFeel: '' } }
    : { kind, form: { description: clampLine(audio?.cueStyle), character: 'natural' } };
}

/** Whether the first line came from the wizard (so the composer can say so). */
export function briefFormIsWizardPrefill(brief: AudioBriefForm, audio: GameDesign['audio'] | undefined): boolean {
  const line = brief.kind === 'music' ? brief.form.mood : brief.form.description;
  const source = clampLine(brief.kind === 'music' ? audio?.musicBrief : audio?.cueStyle);
  return source.length > 0 && line === source;
}

/** The form for a validated brief — how a refresh restores an unsent request. */
export function briefFormFromBrief(ref: AudioBriefRefV1): AudioBriefForm {
  return ref.kind === 'music'
    ? { kind: 'music', form: {
        mood: ref.brief.mood, genre: ref.brief.genre, tempoBpm: String(ref.brief.tempoBpm),
        instrumentation: ref.brief.instrumentation.join(', '), referenceFeel: ref.brief.referenceFeel ?? '',
      } }
    : { kind: 'sfx', form: { description: ref.brief.description, character: ref.brief.character } };
}

/**
 * The closed brief the kickoff receives — validated by the port's own
 * validators, so an invalid form throws the same `PlatformError` the server
 * would. `avoid` is never included: the server owns that line.
 */
export function briefFromForm(brief: AudioBriefForm): AudioBriefRefV1 {
  if (brief.kind === 'music') {
    const f = brief.form;
    const candidate: Record<string, unknown> = {
      mood: f.mood,
      genre: f.genre,
      tempoBpm: /^\d+$/.test(f.tempoBpm.trim()) ? Number(f.tempoBpm.trim()) : f.tempoBpm,
      instrumentation: f.instrumentation.split(',').map((s) => s.trim()).filter(Boolean),
      ...(f.referenceFeel.trim() ? { referenceFeel: f.referenceFeel } : {}),
    };
    return { kind: 'music', brief: validateMusicBriefV1(candidate) };
  }
  return { kind: 'sfx', brief: validateSfxBriefV1({ description: brief.form.description, character: brief.form.character }) };
}

/**
 * Why the brief cannot be sent yet, in the validator's words, or null when it
 * can. Folds the saved avoid line in for the rendered-length check only.
 */
export function briefFormProblem(brief: AudioBriefForm, savedAvoid: string | undefined): string | null {
  try {
    const ref = briefFromForm(brief);
    const folded: AudioBriefRefV1 = ref.kind === 'music'
      ? { kind: 'music', brief: withAvoidDirection<MusicBriefV1>(ref.brief, savedAvoid) }
      : { kind: 'sfx', brief: withAvoidDirection<SfxBriefV1>(ref.brief, savedAvoid) };
    renderAudioBriefText(folded);
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'The brief is incomplete.';
  }
}

export const SFX_CHARACTER_LABELS: Record<SfxCharacter, string> = {
  natural: 'Natural — recorded-sounding',
  cartoon: 'Cartoon — exaggerated, playful',
  cinematic: 'Cinematic — weighty, layered',
  retro: 'Retro — chiptune / arcade',
};
export { SFX_CHARACTERS, MAX_INSTRUMENTS };

export const AUDIO_KIND_LABEL: Record<AudioGenerationKind, string> = { music: 'music', sfx: 'sound effect' };

/**
 * Row 1.11 / ADR 0009 Q1: what a creator is told NEXT TO the generate action,
 * in plain language. Provider-neutral on purpose (the port hides vendors and
 * the asset's details show the recorded provider afterwards); honest about
 * training use, about what is and is not sent, and — per Q4 — silent on any
 * watermark claim, because the platform cannot verify one after processing.
 */
export const AUDIO_GENERATION_DISCLOSURE: Record<AudioGenerationKind, string> = {
  music:
    'What you type in this brief is sent to the music provider, which may use it to train and improve its models — ' +
    'there is no opt-out on the plan we use. Nothing else about your game is sent: not its source, design, concept, title or assets. ' +
    'The track is instrumental, about 90 seconds, and loops without a click (not a seamless musical loop). ' +
    'The provider returns compressed (MP3) audio that is re-encoded for the game, so generated music is never lossless. ' +
    'Each generation makes one candidate; nothing regenerates on its own.',
  sfx:
    'What you type in this description is sent to the Gemini model, which returns only bounded synthesizer parameters. ' +
    'Nothing else about your game is sent: not its source, design, concept, title or assets. The sound itself is then rendered locally ' +
    'by the platform’s deterministic synthesizer; Gemini does not return audio. Each generation makes one candidate; nothing regenerates on its own.',
};

/**
 * ADR 0009 Q5: generated audio is an explicit creator action, offered only when
 * the platform flag is on AND the game's first build is live. The reason is
 * shown on the disabled action; the server enforces both.
 */
export function audioGenerateBlocker(
  kind: AudioGenerationKind,
  flags: AudioGenerationFlags,
  activeVersion: number,
): string | null {
  const label = AUDIO_KIND_LABEL[kind];
  if (!audioGenerationEnabled(flags, kind)) {
    return `Generated ${label}s are not enabled on this platform yet. Curated and Upload work exactly as before.`;
  }
  if (!(activeVersion >= 1)) {
    return `Generate a ${label} after this game's first build is live. Curated and Upload are available now.`;
  }
  return null;
}
